import { ApiDashboard, dashboardApi } from '../apis';
import { showFeedback } from '../feedback/feedbackReducer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../organisation/useMe';
import { useDispatch } from 'react-redux';
import { CAN_READ_DASHBOARD } from '../auth/permissions';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';

export const useQueryDashboards = () => {
  const { currentOrganisation, permissions, features } = useMe();

  return useQuery({
    enabled:
      !!currentOrganisation?.id &&
      permissions.includes(CAN_READ_DASHBOARD) &&
      (isFeatureEnabled(ORGANISATION_FEATURE.SANCTIONS, features) ||
        isFeatureEnabled(ORGANISATION_FEATURE.MONITORING, features)),
    staleTime: 1_000,
    queryKey: ['dashboards', currentOrganisation?.id],
    queryFn: () => dashboardApi.findAllDashboards(),
  });
};

export const useSaveDashboards = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (apiDashboard: ApiDashboard) => {
      if (apiDashboard.id) {
        const newDashboard = await dashboardApi.updateDashboard({ apiDashboard });
        dispatch(
          showFeedback({
            message: `Dashboard #${apiDashboard.id} updated!`,
            success: true,
          })
        );
        return newDashboard;
      } else {
        const res = dashboardApi.addDashboard({ apiDashboard });
        dispatch(
          showFeedback({
            message: `Dashboard ${apiDashboard.name} saved!`,
            success: true,
          })
        );
        return res;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['dashboards', currentOrganisation?.id],
      });
    },
  });
};

export const useDeleteDashboards = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (id: number) => {
      await dashboardApi.deleteDashboard({ id });
      dispatch(
        showFeedback({
          message: `Dashboard #${id} deleted!`,
          success: true,
        })
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['dashboards', currentOrganisation?.id],
      });
    },
  });
};
